/**
 * Image Lightbox Component
 * A simple, accessible lightbox for viewing images in full screen.
 * A [data-lightbox] trigger inside a [data-lightbox-group] container opens a
 * gallery of every trigger in that container, in document order.
 */

import { lockBodyScroll, unlockBodyScroll } from '../modules/scroll-lock.js';
import { DISMISS_ANIMATION_MS, backdropColor, dismissBackdropOpacity, dismissExitTransform, dismissTransform, dragAxis, dragVelocity, pinchZoomed, releaseTransition, shouldDismiss, trackDrag } from '../modules/photo-dismiss.js';
import { prefersReducedMotion } from '../modules/reduced-motion.js';

const ImageLightbox = (function() {
  let lightboxElement = null;
  let imageElement = null;
  let closeButton = null;
  let prevButton = null;
  let nextButton = null;
  let counterElement = null;
  let counterText = null;
  let isOpen = false;
  let items = [];
  let index = 0;
  let drag = null;
  let dismissTimer = 0;

  /** Horizontal travel, in px, that a touch must exceed to count as a swipe. */
  const SWIPE_MIN_PX = 50;
  /** The backdrop alpha of the bg-black/90 class. */
  const BACKDROP_ALPHA = 0.9;

  /**
   * Create the lightbox DOM elements
   */
  function createLightbox() {
    if (lightboxElement) return;

    // Create backdrop
    lightboxElement = document.createElement('div');
    lightboxElement.id = 'image-lightbox';
    lightboxElement.setAttribute('data-img-error-own', '');
    lightboxElement.className = 'fixed inset-0 z-50 hidden items-center justify-center bg-black/90 backdrop-blur-sm';
    lightboxElement.setAttribute('role', 'dialog');
    lightboxElement.setAttribute('aria-modal', 'true');
    lightboxElement.setAttribute('aria-label', 'Image viewer');

    // Create close button
    closeButton = document.createElement('button');
    closeButton.className = 'absolute top-4 end-4 z-10 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm border border-white/20';
    closeButton.setAttribute('aria-label', 'Close image viewer');
    closeButton.innerHTML = '<i data-lucide="x" class="h-5 w-5"></i>';

    // Logical start/end positions and mirrored chevrons follow the reading direction.
    const navClass = 'absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white transition-colors hover:bg-black/70';
    prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = navClass + ' start-2 sm:start-4';
    prevButton.setAttribute('aria-label', window.tOr ? window.tOr('common.photo_previous', 'Previous photo') : 'Previous photo');
    prevButton.innerHTML = '<i data-lucide="chevron-left" class="h-6 w-6 rtl:-scale-x-100"></i>';

    nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = navClass + ' end-2 sm:end-4';
    nextButton.setAttribute('aria-label', window.tOr ? window.tOr('common.photo_next', 'Next photo') : 'Next photo');
    nextButton.innerHTML = '<i data-lucide="chevron-right" class="h-6 w-6 rtl:-scale-x-100"></i>';

    counterElement = document.createElement('div');
    counterElement.className = 'absolute top-4 start-4 z-10 hidden rounded-full bg-black/60 px-3 py-2 text-sm leading-6 tabular-nums text-white';
    counterElement.setAttribute('aria-live', 'polite');
    // The badge keeps the page direction for its position. The text alone is LTR, or an RTL page reads "3 / 2".
    counterText = document.createElement('span');
    counterText.setAttribute('dir', 'ltr');
    counterElement.appendChild(counterText);

    // Create image container
    const imageContainer = document.createElement('div');
    imageContainer.className = 'relative max-w-[90vw] max-h-[90vh] flex items-center justify-center';

    // Create image
    imageElement = document.createElement('img');
    imageElement.className = 'max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl select-none';
    imageElement.setAttribute('alt', 'Full size image');

    // Create loading spinner
    const spinner = document.createElement('div');
    spinner.id = 'lightbox-spinner';
    spinner.className = 'absolute inset-0 flex items-center justify-center';
    spinner.innerHTML = '<i class="fa fa-spinner fa-spin text-white text-3xl"></i>';

    // Assemble
    imageContainer.appendChild(spinner);
    imageContainer.appendChild(imageElement);
    lightboxElement.appendChild(closeButton);
    lightboxElement.appendChild(prevButton);
    lightboxElement.appendChild(nextButton);
    lightboxElement.appendChild(counterElement);
    lightboxElement.appendChild(imageContainer);

    document.body.appendChild(lightboxElement);
    window.LucideIcons?.initialize(lightboxElement);

    // Bind events
    bindEvents();
  }

  /**
   * Bind event listeners
   */
  function bindEvents() {
    // Close on backdrop click
    lightboxElement.addEventListener('click', function(e) {
      if (e.target === lightboxElement) {
        close();
      }
    });

    // Close button
    closeButton.addEventListener('click', close);

    prevButton.addEventListener('click', function() { step(-1); });
    nextButton.addEventListener('click', function() { step(1); });

    // Keyboard navigation
    document.addEventListener('keydown', function(e) {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        close();
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !e.altKey && !e.ctrlKey && !e.metaKey) {
        // The arrow that points at the next photo depends on the reading direction.
        const forward = isRtl() ? 'ArrowLeft' : 'ArrowRight';
        if (step(e.key === forward ? 1 : -1)) {
          e.preventDefault();
        }
      }
    });

    // A horizontal drag steps through the gallery. A vertical drag closes the lightbox (modules/photo-dismiss.js).
    lightboxElement.addEventListener('touchstart', function(e) {
      if (e.touches.length !== 1) {
        cancelDrag();
        return;
      }
      // A drag on a pinch-zoomed page pans the photo.
      if (!isOpen || dismissTimer || pinchZoomed()) return;
      const touch = e.touches[0];
      drag = { x: touch.clientX, y: touch.clientY, axis: null, samples: trackDrag([], touch.clientY, e.timeStamp) };
    }, { passive: true });

    // Not passive: a drag past the axis lock must not scroll the page or pull to refresh it.
    lightboxElement.addEventListener('touchmove', function(e) {
      if (!drag) return;
      if (e.touches.length !== 1) {
        cancelDrag();
        return;
      }
      const touch = e.touches[0];
      const dy = touch.clientY - drag.y;
      drag.axis = drag.axis || dragAxis(touch.clientX - drag.x, dy);
      drag.samples = trackDrag(drag.samples, touch.clientY, e.timeStamp);
      if (drag.axis !== null && e.cancelable) e.preventDefault();
      if (drag.axis === 'vertical') {
        imageElement.style.transition = 'none';
        imageElement.style.transform = dismissTransform(dy, window.innerHeight);
        lightboxElement.style.transition = 'none';
        lightboxElement.style.backgroundColor = backdropColor(dismissBackdropOpacity(dy, window.innerHeight), BACKDROP_ALPHA);
        setChromeHidden(true);
      }
    }, { passive: false });

    lightboxElement.addEventListener('touchend', function(e) {
      if (!drag || e.changedTouches.length !== 1) return;
      const dx = e.changedTouches[0].clientX - drag.x;
      const dy = e.changedTouches[0].clientY - drag.y;
      const axis = drag.axis || dragAxis(dx, dy);
      const velocity = dragVelocity(drag.samples, e.timeStamp);
      drag = null;
      if (axis === 'vertical') {
        if (shouldDismiss(dy, velocity, window.innerHeight)) dismiss(dy);
        else springBack();
      } else if (axis === 'horizontal' && Math.abs(dx) > SWIPE_MIN_PX) {
        // A swipe toward the start edge brings the next photo in.
        const towardStart = isRtl() ? dx > 0 : dx < 0;
        step(towardStart ? 1 : -1);
      }
    }, { passive: true });

    lightboxElement.addEventListener('touchcancel', cancelDrag, { passive: true });

    // Hide spinner when image loads
    imageElement.addEventListener('load', function() {
      const spinner = document.getElementById('lightbox-spinner');
      if (spinner) {
        spinner.classList.add('hidden');
      }
      // Ensure image is visible
      imageElement.style.opacity = '1';
    });

    // Handle image load errors
    imageElement.addEventListener('error', function() {
      const spinner = document.getElementById('lightbox-spinner');
      if (spinner) {
        spinner.classList.remove('hidden');
        spinner.innerHTML = '<div class="text-white text-center"><i class="fa fa-exclamation-triangle text-3xl mb-2"></i><p>' + window.tOr('common.image_load_failed', 'Failed to load image') + '</p></div>';
      }
      imageElement.style.display = 'none';
    });
  }

  function setChromeHidden(hidden) {
    [closeButton, prevButton, nextButton, counterElement].forEach(function(el) {
      el.classList.toggle('opacity-0', hidden);
      el.classList.toggle('pointer-events-none', hidden);
    });
  }

  /** A second touch or a touchcancel springs a vertical drag back. */
  function cancelDrag() {
    if (drag && drag.axis === 'vertical') springBack();
    drag = null;
  }

  function springBack() {
    imageElement.style.transition = releaseTransition('transform');
    imageElement.style.transform = '';
    lightboxElement.style.transition = releaseTransition('background-color');
    lightboxElement.style.backgroundColor = '';
    setChromeHidden(false);
  }

  function dismiss(dy) {
    if (prefersReducedMotion()) {
      close();
      return;
    }
    imageElement.style.transition = releaseTransition('transform');
    imageElement.style.transform = dismissExitTransform(dy, window.innerHeight);
    lightboxElement.style.transition = releaseTransition('background-color');
    lightboxElement.style.backgroundColor = backdropColor(0, BACKDROP_ALPHA);
    dismissTimer = setTimeout(close, DISMISS_ANIMATION_MS);
  }

  function resetDrag() {
    drag = null;
    clearTimeout(dismissTimer);
    dismissTimer = 0;
    imageElement.style.transition = '';
    imageElement.style.transform = '';
    lightboxElement.style.transition = '';
    lightboxElement.style.backgroundColor = '';
    setChromeHidden(false);
  }

  function isRtl() {
    return document.documentElement.getAttribute('dir') === 'rtl';
  }

  /**
   * Show the photo at position `i` of the open gallery.
   */
  function show(i) {
    index = i;
    const item = items[index];

    // Show spinner, reset image state
    const spinner = document.getElementById('lightbox-spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
      spinner.innerHTML = '<i class="fa fa-spinner fa-spin text-white text-3xl"></i>';
    }
    imageElement.style.display = '';

    // Set image source
    imageElement.src = item.src;
    imageElement.setAttribute('alt', item.alt || 'Full size image');

    const gallery = items.length > 1;
    counterText.textContent = gallery ? (index + 1) + ' / ' + items.length : '';
    counterElement.classList.toggle('hidden', !gallery);
    toggleNav(prevButton, gallery && index > 0);
    toggleNav(nextButton, gallery && index < items.length - 1);
    // A nav button that hides at an end must not drop the focus out of the dialog.
    if (document.activeElement && document.activeElement.classList && document.activeElement.classList.contains('hidden')) {
      closeButton.focus();
    }

    // Warm the cache for the neighbours, so a step shows the photo at once.
    [index - 1, index + 1].forEach(function(n) {
      if (items[n]) {
        new Image().src = items[n].src;
      }
    });
  }

  function toggleNav(button, visible) {
    button.classList.toggle('hidden', !visible);
    button.classList.toggle('flex', visible);
  }

  /**
   * Move by `delta` photos. Returns whether the gallery moved.
   */
  function step(delta) {
    const target = index + delta;
    if (!isOpen || target < 0 || target >= items.length) {
      return false;
    }
    show(target);
    return true;
  }

  /**
   * Open lightbox with specified image
   * @param {string} src - Image source URL
   * @param {string} [alt] - Alt text for the image
   */
  function open(src, alt) {
    openGallery([{ src: src, alt: alt }], 0);
  }

  /**
   * Open lightbox on a list of images, starting at `startIndex`.
   * @param {Array<{src: string, alt?: string}>} galleryItems
   * @param {number} startIndex
   */
  function openGallery(galleryItems, startIndex) {
    createLightbox();

    items = galleryItems;
    show(Math.min(Math.max(startIndex, 0), items.length - 1));

    // Show lightbox
    lightboxElement.classList.remove('hidden');
    lightboxElement.classList.add('flex');
    isOpen = true;

    // Prevent body scroll
    lockBodyScroll();

    // Focus the close button for accessibility
    closeButton.focus();
  }

  /**
   * Close the lightbox
   */
  function close() {
    if (!lightboxElement || !isOpen) return;

    lightboxElement.classList.add('hidden');
    lightboxElement.classList.remove('flex');
    isOpen = false;
    resetDrag();

    // Restore body scroll
    unlockBodyScroll();

    // Clear image source
    imageElement.src = '';
    items = [];
    index = 0;
  }

  function itemFor(trigger) {
    return {
      src: trigger.getAttribute('data-lightbox') || trigger.getAttribute('href') || trigger.src,
      alt: trigger.getAttribute('alt') || trigger.getAttribute('title') || 'Image'
    };
  }

  /**
   * Initialize lightbox on images with data-lightbox attribute
   */
  function init() {
    // Use event delegation for dynamically added images
    document.addEventListener('click', function(e) {
      const trigger = e.target.closest('[data-lightbox]');
      if (trigger) {
        e.preventDefault();
        const group = trigger.closest('[data-lightbox-group]');
        const triggers = group ? Array.from(group.querySelectorAll('[data-lightbox]')) : [trigger];
        openGallery(triggers.map(itemFor), triggers.indexOf(trigger));
      }
    });
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  return {
    open: open,
    openGallery: openGallery,
    close: close,
    init: init
  };
})();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ImageLightbox;
}

// Make available globally
window.ImageLightbox = ImageLightbox;
